<template>
  <teleport to="body">
    <transition name="chat-window">
      <div
        v-if="hasOpened"
        v-show="isOpen"
        ref="chatWindowRef"
        class="custom-webchat-window"
      >
        <iframe
          id="webchat-frame"
          ref="chatFrameRef"
          class="custom-webchat-frame"
          :src="webchatFrameSrc"
          :title="assistantLabel"
          allow="microphone; clipboard-write"
          @load="handleFrameLoad"
        ></iframe>
      </div>
    </transition>

    <div
      ref="launcherRef"
      class="floating-launcher-shell"
      :class="{ mobile: isMobileViewport }"
      :style="launcherPositionStyle"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <transition name="launcher-label">
        <span v-if="showAssistantLabel" class="launcher-label">
          {{ assistantLabel }}
        </span>
      </transition>

      <button
        class="floating-launcher"
        :class="{
          active: isOpen,
          loading: isLoading,
          dragging: isDragging,
          'has-notification': showNotificationBadge,
        }"
        type="button"
        :aria-label="assistantAriaLabel"
        :aria-expanded="isOpen ? 'true' : 'false'"
        @click="handleLauncherClick"
        @pointerdown="handlePointerDown"
      >
        <transition name="notification-badge">
          <span
            v-if="showNotificationBadge"
            class="launcher-notification"
            :class="{ empty: unreadCount === 0 }"
            aria-hidden="true"
          >
            {{ notificationBadgeText }}
          </span>
        </transition>
        <img
          class="launcher-avatar"
          :src="launcherImage"
          alt=""
          aria-hidden="true"
        />
      </button>
    </div>
  </teleport>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import launcherImage from "@/assets/images/ai-chatbot-launcher.jpeg";

const HIDE_RETRY_DELAYS = [0, 500, 1500];
const MOBILE_BREAKPOINT = 768;
const MOBILE_STORAGE_KEY = "chatbot-launcher-position";
const DEFAULT_DESKTOP_OFFSET = { right: 18, bottom: 20 };
const DEFAULT_MOBILE_OFFSET = { right: 18, bottom: 20 };
const MIN_VIEWPORT_PADDING = 12;
const DRAG_START_THRESHOLD = 6;
const WEBCHAT_ORIGIN = "https://chat.agents.mn";
const WEBCHAT_ORG_UUID = "dOPDItsyWkAA8RBt";
const WEBCHAT_ORG_ID = "20422";
const WEBCHAT_AGENT_ID = 3192;
const WEBCHAT_CHANNEL_ID = 404;
const WEBCHAT_PLATFORM = "webchat";
const WEBCHAT_CONVERSATION_KEY = "kfc-webchat-conversation-key";
const WEBCHAT_CONVERSATION_ID_KEY = "kfc-webchat-conversation-id";
const WEBCHAT_UNREAD_COUNT_KEY = "kfc-webchat-unread-count";
const WEBCHAT_NOTIFICATION_EVENT = "kfc:webchat-notification";
const WEBSOCKET_RECONNECT_DELAYS = [1000, 2000, 5000, 10000];
const MAX_WEBSOCKET_RECONNECT_ATTEMPTS = 5;
const MAX_NOTIFICATION_COUNT = 100;

const getWidgetConfig = () => ({
  baseUrl: WEBCHAT_ORIGIN,
  channelID: WEBCHAT_CHANNEL_ID,
  url: `${WEBCHAT_ORIGIN}/o/${WEBCHAT_ORG_UUID}`,
});

const getWebChatBaseUrl = () => getWidgetConfig().baseUrl || WEBCHAT_ORIGIN;

const getWebSocketBaseUrl = () =>
  getWebChatBaseUrl().replace(/^http/, "ws");

const isLocalDevServer = () =>
  process.env.NODE_ENV !== "production" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const parseSocketMessage = (data) => {
  try {
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch (error) {
    return null;
  }
};

const getIncomingMessage = (messageEvent) =>
  messageEvent?.data?.message || messageEvent?.message || null;

const getIncomingConversation = (messageEvent) =>
  messageEvent?.data?.conversation || messageEvent?.conversation || null;

const getIncomingConversationId = (messageEvent) => {
  const message = getIncomingMessage(messageEvent);
  const conversation = getIncomingConversation(messageEvent);

  return String(
    messageEvent?.data?.conversation_id ||
      messageEvent?.conversation_id ||
      message?.conversation_id ||
      conversation?.id ||
      ""
  );
};

const getIncomingConversationKey = (messageEvent) => {
  const message = getIncomingMessage(messageEvent);
  const conversation = getIncomingConversation(messageEvent);

  return String(
    messageEvent?.data?.platform_room_id ||
      messageEvent?.platform_room_id ||
      message?.platform_room_id ||
      conversation?.platform_room_id ||
      ""
  );
};

const getNotificationKey = (messageEvent) => {
  const message = getIncomingMessage(messageEvent);

  return String(
    message?.id ||
      messageEvent?.data?.message_id ||
      messageEvent?.message_id ||
      getIncomingConversationId(messageEvent) ||
      getIncomingConversationKey(messageEvent) ||
      messageEvent?.type ||
      ""
  );
};

const isIncomingAssistantMessage = (messageEvent) => {
  if (!messageEvent || typeof messageEvent !== "object") {
    return false;
  }

  if (
    messageEvent.type === "chunk" ||
    messageEvent.type === "generating_message"
  ) {
    return true;
  }

  if (messageEvent.type !== "message_added") {
    return false;
  }

  const message = getIncomingMessage(messageEvent);
  return Boolean(
    message &&
      (message.role === "ai_assistant" ||
        message.role === "human_assistant" ||
        message.role === "trigger")
  );
};

const hideNativeWidgetControls = (widget) => {
  if (!widget?.elements) {
    return;
  }

  widget.elements.container?.classList.add("kfc-custom-webchat");

  if (widget.elements.button) {
    widget.elements.button.style.display = "none";
  }

  if (widget.elements.greeting) {
    widget.elements.greeting.style.display = "none";
  }
};

const syncWidgetState = (widget, isOpen) => {
  hideNativeWidgetControls(widget);
  isOpen.value = Boolean(widget?.state?.isOpen);
};

const applyWidgetLanguageSafely = (widget, langCode) => {
  if (!widget || !langCode) {
    return;
  }

  widget.state = widget.state || {};
  widget.state.currentLang = langCode;
  localStorage.setItem("languageCode", langCode);

  const greetingText = widget.elements?.greeting?.querySelector?.(
    ".webchat-greeting-text"
  );
  if (greetingText && typeof widget.getLocalizedText === "function") {
    greetingText.textContent = widget.getLocalizedText(langCode);
  }

  const iframe =
    widget.elements?.iframe || document.getElementById("webchat-frame");
  if (iframe?.contentWindow) {
    iframe.contentWindow.postMessage(
      {
        type: "change_lang",
        variables: {
          langCode,
        },
      },
      getWebChatBaseUrl()
    );
  }
};

const patchWidgetInstance = (widget, isOpen) => {
  if (!widget || widget.__kfcPatched) {
    syncWidgetState(widget, isOpen);
    return widget;
  }

  const originalToggle = widget.toggleChat.bind(widget);

  widget.toggleChat = (...args) => {
    const result = originalToggle(...args);
    syncWidgetState(widget, isOpen);
    return result;
  };

  widget.changeLanguage = (langCode) => {
    applyWidgetLanguageSafely(widget, langCode);
  };

  widget.__kfcPatched = true;
  syncWidgetState(widget, isOpen);
  return widget;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getStoredUnreadCount = () => {
  const storedCount = Number(localStorage.getItem(WEBCHAT_UNREAD_COUNT_KEY));
  if (!Number.isFinite(storedCount)) {
    return 0;
  }

  return clamp(Math.trunc(storedCount), 0, MAX_NOTIFICATION_COUNT);
};

const getExplicitNotificationCount = (messageEvent) => {
  const explicitCount =
    messageEvent?.data?.count ??
    messageEvent?.data?.unreadCount ??
    messageEvent?.data?.badgeCount ??
    messageEvent?.count ??
    messageEvent?.unreadCount ??
    messageEvent?.badgeCount;

  if (explicitCount === undefined || explicitCount === null) {
    return null;
  }

  const parsedCount = Number(explicitCount);
  if (!Number.isFinite(parsedCount)) {
    return null;
  }

  return clamp(Math.trunc(parsedCount), 0, MAX_NOTIFICATION_COUNT);
};

export default {
  name: "ChatbotWidget",
  setup() {
    const store = useStore();
    const locale = computed(() => store.getters.locale);
    const launcherRef = ref(null);
    const chatWindowRef = ref(null);
    const chatFrameRef = ref(null);
    const hasOpened = ref(false);
    const isFrameReady = ref(false);
    const isLoading = ref(false);
    const isOpen = ref(false);
    const isDragging = ref(false);
    const isHovered = ref(false);
    const unreadCount = ref(getStoredUnreadCount());
    const lastNotificationKey = ref("");
    const webchatConversationKey = ref(
      localStorage.getItem(WEBCHAT_CONVERSATION_KEY) || ""
    );
    const webchatConversationId = ref(
      localStorage.getItem(WEBCHAT_CONVERSATION_ID_KEY) || ""
    );
    const isMobileViewport = ref(false);
    const suppressNextToggle = ref(false);
    const launcherPosition = ref({ ...DEFAULT_DESKTOP_OFFSET });
    const loadTimeouts = [];
    const dragState = {
      pointerId: null,
      startX: 0,
      startY: 0,
      initialRight: 0,
      initialBottom: 0,
      moved: false,
    };
    let syncIntervalId = null;
    let notificationSocket = null;
    let notificationReconnectTimeoutId = null;
    let notificationReconnectAttempt = 0;
    let notificationSocketKey = "";
    let notificationSocketManuallyClosed = false;
    let orgNotificationSocket = null;
    let orgNotificationReconnectTimeoutId = null;
    let orgNotificationReconnectAttempt = 0;
    let orgNotificationSocketManuallyClosed = false;

    const assistantLabel = computed(() =>
      locale.value === "en" ? "AI Assistant" : "AI туслах"
    );
    const assistantAriaLabel = computed(() =>
      isOpen.value
        ? assistantLabel.value
        : unreadCount.value > 0
        ? locale.value === "en"
          ? `Open AI assistant, ${notificationCount.value} unread notification${
              unreadCount.value === 1 ? "" : "s"
            }`
          : `AI туслах нээх, ${notificationCount.value} шинэ мэдэгдэлтэй`
        : locale.value === "en"
        ? "Open AI assistant"
        : "AI туслах нээх"
    );
    const launcherPositionStyle = computed(() => ({
      right: `${launcherPosition.value.right}px`,
      bottom: `${launcherPosition.value.bottom}px`,
    }));
    const webchatFrameSrc = computed(
      () =>
        `${WEBCHAT_ORIGIN}/o/${WEBCHAT_ORG_UUID}/a/${WEBCHAT_AGENT_ID}?channel_id=${WEBCHAT_CHANNEL_ID}`
    );
    const showAssistantLabel = computed(
      () =>
        isHovered.value &&
        !isOpen.value &&
        !isDragging.value &&
        !isLoading.value &&
        !isMobileViewport.value
    );
    const notificationCount = computed(() =>
      unreadCount.value > 99 ? "99+" : unreadCount.value
    );
    const notificationBadgeText = computed(() =>
      unreadCount.value > 0 ? notificationCount.value : ""
    );
    const showNotificationBadge = computed(
      () => !isOpen.value
    );

    const clearNotifications = () => {
      unreadCount.value = 0;
      lastNotificationKey.value = "";
    };

    const markIncomingMessage = (messageEvent) => {
      if (isOpen.value) {
        return;
      }

      const explicitCount = getExplicitNotificationCount(messageEvent);
      if (explicitCount !== null) {
        unreadCount.value = explicitCount;
        lastNotificationKey.value = getNotificationKey(messageEvent);
        return;
      }

      const notificationKey = getNotificationKey(messageEvent);
      if (
        notificationKey &&
        notificationKey === lastNotificationKey.value
      ) {
        return;
      }

      lastNotificationKey.value = notificationKey;
      unreadCount.value = Math.min(
        unreadCount.value + 1,
        MAX_NOTIFICATION_COUNT
      );
    };

    const clearNotificationReconnect = () => {
      if (notificationReconnectTimeoutId) {
        window.clearTimeout(notificationReconnectTimeoutId);
        notificationReconnectTimeoutId = null;
      }
    };

    const closeNotificationSocket = () => {
      clearNotificationReconnect();
      notificationSocketManuallyClosed = true;

      if (notificationSocket) {
        notificationSocket.close();
        notificationSocket = null;
      }
    };

    const getWebchatOrgId = () => WEBCHAT_ORG_ID;

    const setWebchatConversation = ({ conversationKey, conversationId }) => {
      if (conversationKey) {
        webchatConversationKey.value = String(conversationKey);
        localStorage.setItem(
          WEBCHAT_CONVERSATION_KEY,
          webchatConversationKey.value
        );
        connectNotificationSocket(webchatConversationKey.value);
      }

      if (conversationId) {
        webchatConversationId.value = String(conversationId);
        localStorage.setItem(
          WEBCHAT_CONVERSATION_ID_KEY,
          webchatConversationId.value
        );
      }
    };

    const isCurrentWebchatConversation = (conversation) => {
      if (!conversation) {
        return true;
      }

      if (
        conversation.platform &&
        conversation.platform !== WEBCHAT_PLATFORM
      ) {
        return false;
      }

      return (
        !conversation.channel_id ||
        String(conversation.channel_id) === String(WEBCHAT_CHANNEL_ID)
      );
    };

    const rememberConversationFromEvent = (messageEvent) => {
      const conversation = getIncomingConversation(messageEvent);
      if (!isCurrentWebchatConversation(conversation)) {
        return;
      }

      const conversationKey = getIncomingConversationKey(messageEvent);
      const conversationId = getIncomingConversationId(messageEvent);
      if (!conversationKey && !conversationId) {
        return;
      }

      setWebchatConversation({
        conversationKey,
        conversationId,
      });
    };

    const isActiveConversationEvent = (
      messageEvent,
      allowMissingConversation = true
    ) => {
      const conversation = getIncomingConversation(messageEvent);
      if (!isCurrentWebchatConversation(conversation)) {
        return false;
      }

      const conversationKey = getIncomingConversationKey(messageEvent);
      const conversationId = getIncomingConversationId(messageEvent);

      if (
        conversationKey &&
        webchatConversationKey.value &&
        conversationKey === webchatConversationKey.value
      ) {
        return true;
      }

      if (
        conversationId &&
        webchatConversationId.value &&
        conversationId === webchatConversationId.value
      ) {
        return true;
      }

      return allowMissingConversation && !conversationKey && !conversationId;
    };

    const shouldAcceptDevConversationEvent = (messageEvent) => {
      if (
        !isLocalDevServer() ||
        webchatConversationKey.value ||
        webchatConversationId.value
      ) {
        return false;
      }

      const conversation = getIncomingConversation(messageEvent);
      if (!isCurrentWebchatConversation(conversation)) {
        return false;
      }

      return Boolean(
        getIncomingConversationKey(messageEvent) ||
          getIncomingConversationId(messageEvent)
      );
    };

    const handleNotificationSocketMessage = (event) => {
      const messageEvent = parseSocketMessage(event.data);
      if (!isIncomingAssistantMessage(messageEvent)) {
        return;
      }

      rememberConversationFromEvent(messageEvent);
      markIncomingMessage(messageEvent);
    };

    const scheduleNotificationReconnect = () => {
      if (!webchatConversationKey.value) {
        return;
      }

      if (notificationReconnectAttempt >= MAX_WEBSOCKET_RECONNECT_ATTEMPTS) {
        return;
      }

      const delay =
        WEBSOCKET_RECONNECT_DELAYS[
          Math.min(
            notificationReconnectAttempt,
            WEBSOCKET_RECONNECT_DELAYS.length - 1
          )
        ];
      notificationReconnectAttempt += 1;
      clearNotificationReconnect();
      notificationReconnectTimeoutId = window.setTimeout(() => {
        connectNotificationSocket(webchatConversationKey.value);
      }, delay);
    };

    const connectNotificationSocket = async (conversationKey) => {
      if (!conversationKey) {
        return;
      }

      if (
        notificationSocketKey === conversationKey &&
        notificationSocket &&
        [
          WebSocket.CONNECTING,
          WebSocket.OPEN,
        ].includes(notificationSocket.readyState)
      ) {
        return;
      }

      closeNotificationSocket();
      notificationSocketManuallyClosed = false;
      notificationSocketKey = conversationKey;

      const orgId = getWebchatOrgId();
      if (!orgId || notificationSocketKey !== conversationKey) {
        return;
      }

      const socketUrl = `${getWebSocketBaseUrl()}/m/conv-ctrl/pub/orgs/${orgId}/conversations/${WEBCHAT_PLATFORM}/${encodeURIComponent(
        conversationKey
      )}/ws`;
      const socket = new WebSocket(socketUrl);
      notificationSocket = socket;
      let socketHadError = false;

      socket.onopen = () => {
        notificationReconnectAttempt = 0;
      };
      socket.onmessage = handleNotificationSocketMessage;
      socket.onclose = (closeEvent) => {
        if (notificationSocket !== socket) {
          return;
        }

        notificationSocket = null;

        if (
          !notificationSocketManuallyClosed &&
          (socketHadError || !closeEvent.wasClean)
        ) {
          scheduleNotificationReconnect();
        }
      };
      socket.onerror = () => {
        socketHadError = true;
        socket.close();
      };
    };

    const clearOrgNotificationReconnect = () => {
      if (orgNotificationReconnectTimeoutId) {
        window.clearTimeout(orgNotificationReconnectTimeoutId);
        orgNotificationReconnectTimeoutId = null;
      }
    };

    const closeOrgNotificationSocket = () => {
      clearOrgNotificationReconnect();
      orgNotificationSocketManuallyClosed = true;

      if (orgNotificationSocket) {
        orgNotificationSocket.close();
        orgNotificationSocket = null;
      }
    };

    const handleOrgNotificationSocketMessage = (event) => {
      const messageEvent = parseSocketMessage(event.data);

      if (
        !isActiveConversationEvent(messageEvent, false) &&
        !shouldAcceptDevConversationEvent(messageEvent)
      ) {
        return;
      }

      rememberConversationFromEvent(messageEvent);

      if (isIncomingAssistantMessage(messageEvent)) {
        markIncomingMessage(messageEvent);
      }
    };

    const scheduleOrgNotificationReconnect = () => {
      if (
        orgNotificationReconnectAttempt >= MAX_WEBSOCKET_RECONNECT_ATTEMPTS
      ) {
        return;
      }

      const delay =
        WEBSOCKET_RECONNECT_DELAYS[
          Math.min(
            orgNotificationReconnectAttempt,
            WEBSOCKET_RECONNECT_DELAYS.length - 1
          )
        ];
      orgNotificationReconnectAttempt += 1;
      clearOrgNotificationReconnect();
      orgNotificationReconnectTimeoutId = window.setTimeout(() => {
        connectOrgNotificationSocket();
      }, delay);
    };

    const connectOrgNotificationSocket = async () => {
      if (
        orgNotificationSocket &&
        [
          WebSocket.CONNECTING,
          WebSocket.OPEN,
        ].includes(orgNotificationSocket.readyState)
      ) {
        return;
      }

      closeOrgNotificationSocket();
      orgNotificationSocketManuallyClosed = false;

      const orgId = getWebchatOrgId();
      if (!orgId) {
        return;
      }

      const socket = new WebSocket(
        `${getWebSocketBaseUrl()}/m/conv-ctrl/pub/orgs/${orgId}/ws`
      );
      orgNotificationSocket = socket;
      let socketHadError = false;

      socket.onopen = () => {
        orgNotificationReconnectAttempt = 0;
      };
      socket.onmessage = handleOrgNotificationSocketMessage;
      socket.onclose = (closeEvent) => {
        if (orgNotificationSocket !== socket) {
          return;
        }

        orgNotificationSocket = null;

        if (
          !orgNotificationSocketManuallyClosed &&
          (socketHadError || !closeEvent.wasClean)
        ) {
          scheduleOrgNotificationReconnect();
        }
      };
      socket.onerror = () => {
        socketHadError = true;
        socket.close();
      };
    };

    const handleWebchatWindowMessage = (event) => {
      if (event.origin !== WEBCHAT_ORIGIN) {
        return;
      }

      const data = event.data;
      if (data?.type === "iframeReady") {
        applyWidgetLocale();
        sendFrameConfig();
        return;
      }

      if (data?.type === "conversationCreated") {
        setWebchatConversation({
          conversationKey: data.platform_room_id,
          conversationId: data.conversation_id,
        });
        return;
      }

      if (
        isIncomingAssistantMessage(data) &&
        isActiveConversationEvent(data)
      ) {
        rememberConversationFromEvent(data);
        markIncomingMessage(data);
      }
    };

    const handleWebchatNotificationEvent = (event) => {
      const messageEvent = event.detail || {
        type: "message_added",
        message: {
          id: `notification-${Date.now()}`,
          role: "ai_assistant",
        },
      };

      if (messageEvent.clear || messageEvent.type === "clear") {
        clearNotifications();
        return;
      }

      rememberConversationFromEvent(messageEvent);
      markIncomingMessage(messageEvent);
    };

    const setWidgetOffsets = () => {
      document.documentElement.style.setProperty("--position-right", "16px");
      document.documentElement.style.setProperty("--position-bottom", "18px");
    };

    const postToChatFrame = (payload) => {
      chatFrameRef.value?.contentWindow?.postMessage(payload, WEBCHAT_ORIGIN);
    };

    const sendFrameConfig = () => {
      postToChatFrame({
        type: "webchat_config",
        config: {
          history: false,
          showFooter: true,
          allowAttachments: true,
        },
      });
    };

    const applyWidgetLocale = () => {
      if (!locale.value) {
        return;
      }

      localStorage.setItem("languageCode", locale.value);
      postToChatFrame({
        type: "change_lang",
        variables: {
          langCode: locale.value,
        },
      });
    };

    const handleFrameLoad = () => {
      isFrameReady.value = true;
      isLoading.value = false;
      applyWidgetLocale();
      sendFrameConfig();
    };

    const scheduleHideRetries = (widget) => {
      if (!widget || widget.__kfcHideScheduled) {
        return;
      }

      widget.__kfcHideScheduled = true;

      HIDE_RETRY_DELAYS.forEach((delay) => {
        const timeoutId = window.setTimeout(() => {
          hideNativeWidgetControls(widget);
        }, delay);
        loadTimeouts.push(timeoutId);
      });
    };

    const initializeWidget = () => {
      connectOrgNotificationSocket();

      if (webchatConversationKey.value) {
        connectNotificationSocket(webchatConversationKey.value);
      }

      return null;
    };

    const syncWidget = () => {
      initializeWidget();
      isLoading.value = false;
    };

    const closeChat = () => {
      isOpen.value = false;
    };

    const getLauncherBounds = () => {
      const rect = launcherRef.value?.getBoundingClientRect();
      return {
        width: rect?.width || 56,
        height: rect?.height || 56,
      };
    };

    const getDefaultPosition = () =>
      isMobileViewport.value
        ? { ...DEFAULT_MOBILE_OFFSET }
        : { ...DEFAULT_DESKTOP_OFFSET };

    const clampLauncherPosition = (position) => {
      const { width, height } = getLauncherBounds();
      const maxRight = Math.max(
        MIN_VIEWPORT_PADDING,
        window.innerWidth - width - MIN_VIEWPORT_PADDING
      );
      const maxBottom = Math.max(
        MIN_VIEWPORT_PADDING,
        window.innerHeight - height - MIN_VIEWPORT_PADDING
      );

      return {
        right: clamp(position.right, MIN_VIEWPORT_PADDING, maxRight),
        bottom: clamp(position.bottom, MIN_VIEWPORT_PADDING, maxBottom),
      };
    };

    const saveMobilePosition = () => {
      if (!isMobileViewport.value) {
        return;
      }

      localStorage.setItem(
        MOBILE_STORAGE_KEY,
        JSON.stringify(launcherPosition.value)
      );
    };

    const loadLauncherPosition = () => {
      if (!isMobileViewport.value) {
        launcherPosition.value = getDefaultPosition();
        return;
      }

      try {
        const storedPosition = localStorage.getItem(MOBILE_STORAGE_KEY);
        if (!storedPosition) {
          launcherPosition.value = getDefaultPosition();
          return;
        }

        launcherPosition.value = clampLauncherPosition(
          JSON.parse(storedPosition)
        );
      } catch (error) {
        launcherPosition.value = getDefaultPosition();
      }
    };

    const handleResize = () => {
      isMobileViewport.value = window.innerWidth <= MOBILE_BREAKPOINT;
      loadLauncherPosition();
      launcherPosition.value = clampLauncherPosition(launcherPosition.value);
      setWidgetOffsets();
    };

    const handleMouseEnter = () => {
      isHovered.value = true;
    };

    const handleMouseLeave = () => {
      isHovered.value = false;
    };

    const handleOutsidePointerDown = (event) => {
      if (!isOpen.value) {
        return;
      }

      const target = event.target;

      if (launcherRef.value?.contains(target)) {
        return;
      }

      if (chatWindowRef.value?.contains(target)) {
        return;
      }

      closeChat();
    };

    const stopDragging = () => {
      if (dragState.pointerId === null) {
        return;
      }

      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);

      suppressNextToggle.value = dragState.moved;
      if (dragState.moved) {
        saveMobilePosition();
      }

      dragState.pointerId = null;
      dragState.moved = false;
      isDragging.value = false;
    };

    const handlePointerMove = (event) => {
      if (dragState.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      if (
        !dragState.moved &&
        Math.hypot(deltaX, deltaY) >= DRAG_START_THRESHOLD
      ) {
        dragState.moved = true;
        isDragging.value = true;
      }

      if (!dragState.moved) {
        return;
      }

      launcherPosition.value = clampLauncherPosition({
        right: dragState.initialRight - deltaX,
        bottom: dragState.initialBottom - deltaY,
      });
    };

    const handlePointerDown = (event) => {
      if (!isMobileViewport.value || isOpen.value) {
        return;
      }

      dragState.pointerId = event.pointerId;
      dragState.startX = event.clientX;
      dragState.startY = event.clientY;
      dragState.initialRight = launcherPosition.value.right;
      dragState.initialBottom = launcherPosition.value.bottom;
      dragState.moved = false;

      window.addEventListener("pointermove", handlePointerMove, {
        passive: true,
      });
      window.addEventListener("pointerup", stopDragging);
      window.addEventListener("pointercancel", stopDragging);
    };

    const handleLauncherClick = () => {
      if (suppressNextToggle.value) {
        suppressNextToggle.value = false;
        return;
      }

      if (!hasOpened.value) {
        hasOpened.value = true;
        isLoading.value = !isFrameReady.value;
      }

      isOpen.value = !isOpen.value;
    };

    watch(locale, () => {
      applyWidgetLocale();
    });

    watch(isOpen, (newValue) => {
      if (newValue) {
        clearNotifications();
      }
    });

    watch(unreadCount, (newValue) => {
      if (newValue > 0) {
        localStorage.setItem(WEBCHAT_UNREAD_COUNT_KEY, String(newValue));
        return;
      }

      localStorage.removeItem(WEBCHAT_UNREAD_COUNT_KEY);
    });

    onMounted(() => {
      setWidgetOffsets();
      handleResize();
      syncWidget();

      window.addEventListener("resize", handleResize, { passive: true });
      window.addEventListener("message", handleWebchatWindowMessage);
      window.addEventListener(
        WEBCHAT_NOTIFICATION_EVENT,
        handleWebchatNotificationEvent
      );
      document.addEventListener("pointerdown", handleOutsidePointerDown);
    });

    onBeforeUnmount(() => {
      if (syncIntervalId) {
        window.clearInterval(syncIntervalId);
      }
      loadTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      closeNotificationSocket();
      closeOrgNotificationSocket();
      stopDragging();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("message", handleWebchatWindowMessage);
      window.removeEventListener(
        WEBCHAT_NOTIFICATION_EVENT,
        handleWebchatNotificationEvent
      );
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
    });

    return {
      assistantAriaLabel,
      assistantLabel,
      handleLauncherClick,
      handleFrameLoad,
      handleMouseEnter,
      handleMouseLeave,
      handlePointerDown,
      chatFrameRef,
      chatWindowRef,
      hasOpened,
      isDragging,
      isHovered,
      isLoading,
      isMobileViewport,
      isOpen,
      launcherImage,
      launcherPositionStyle,
      launcherRef,
      unreadCount,
      notificationBadgeText,
      notificationCount,
      showAssistantLabel,
      showNotificationBadge,
      webchatFrameSrc,
    };
  },
};
</script>

<style lang="scss" scoped>
.floating-launcher-shell {
  align-items: center;
  display: inline-flex;
  gap: 12px;
  position: fixed;
  z-index: 10001;
}

.floating-launcher-shell.mobile {
  gap: 0;
}

.floating-launcher {
  align-items: center;
  background: linear-gradient(135deg, #e4002b 0%, #9f001d 100%);
  border: 0;
  border-radius: 999px;
  box-shadow: 0 18px 34px rgba(228, 0, 43, 0.28);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  height: 60px;
  justify-content: center;
  position: relative;
  transition:
    box-shadow 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
  width: 60px;
}

.floating-launcher.has-notification {
  box-shadow:
    0 18px 34px rgba(228, 0, 43, 0.28),
    0 0 0 6px rgba(228, 0, 43, 0.12);
}

.floating-launcher.active {
  box-shadow: 0 20px 36px rgba(87, 15, 28, 0.36);
}

.floating-launcher.loading {
  opacity: 0.78;
}

.floating-launcher.dragging {
  cursor: grabbing;
  transform: scale(1.04);
}

.launcher-avatar {
  border: 2px solid rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  box-sizing: border-box;
  display: block;
  height: 60px;
  object-fit: cover;
  width: 60px;
}

.launcher-label {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(228, 0, 43, 0.12);
  border-radius: 999px;
  box-shadow: 0 18px 30px rgba(15, 23, 42, 0.12);
  color: #121212;
  font-family: "Roboto", sans-serif;
  font-size: 18px;
  line-height: 1;
  padding: 12px 18px;
  white-space: nowrap;
}

.launcher-label-enter-active,
.launcher-label-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.launcher-label-enter-from,
.launcher-label-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

.launcher-notification {
  align-items: center;
  animation: badgeBuzz 1.8s ease-in-out infinite;
  background: #ff2f43;
  border: 2px solid #fff;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", sans-serif;
  font-size: 12px;
  font-weight: 700;
  height: 22px;
  justify-content: center;
  min-width: 22px;
  padding: 0 5px;
  position: absolute;
  right: -3px;
  box-shadow: 0 8px 18px rgba(228, 0, 43, 0.32);
  top: -3px;
  z-index: 1;
}

.launcher-notification.empty {
  height: 14px;
  min-width: 14px;
  padding: 0;
  right: 2px;
  top: 2px;
}

.notification-badge-enter-active,
.notification-badge-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.notification-badge-enter-from,
.notification-badge-leave-to {
  opacity: 0;
  transform: translate(4px, -4px) scale(0.7);
}

@keyframes badgeBuzz {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }

  10% {
    transform: rotate(-10deg) scale(1.06);
  }

  20% {
    transform: rotate(10deg) scale(1.06);
  }

  30% {
    transform: rotate(-6deg) scale(1.03);
  }

  40% {
    transform: rotate(6deg) scale(1.03);
  }

  50% {
    transform: rotate(0deg) scale(1);
  }
}

.custom-webchat-window {
  background: #fff;
  border-radius: 24px;
  bottom: 92px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  height: min(680px, calc(100dvh - 124px));
  overflow: hidden;
  position: fixed;
  right: 16px;
  width: min(420px, calc(100vw - 32px));
  z-index: 10000;
}

.custom-webchat-frame {
  background: #fff;
  border: 0;
  display: block;
  height: 100%;
  width: 100%;
}

.chat-window-enter-active,
.chat-window-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.chat-window-enter-from,
.chat-window-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media (max-width: 768px) {
  .floating-launcher {
    height: 58px;
    touch-action: none;
    width: 58px;
  }

  .launcher-avatar {
    height: 58px;
    width: 58px;
  }

  .launcher-label {
    font-size: 16px;
    padding: 10px 14px;
  }

  .custom-webchat-window {
    border-radius: 24px;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 88px);
    height: min(72dvh, 620px);
    left: 12px;
    max-height: calc(100dvh - 116px);
    right: 12px;
    width: auto;
  }
}
</style>
